import { useCallback, useEffect, useRef, useState } from 'react';
import { pointApi } from '../services/point';

export function usePointCobro(boletaId, onApproved) {
  const [enabled, setEnabled] = useState(false);
  const [terminales, setTerminales] = useState([]);
  const [terminalId, setTerminalId] = useState('');
  const [medioPago, setMedioPago] = useState('all');
  const [cobro, setCobro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(null);
  const generation = useRef(0);
  const submitting = useRef(false);
  const notified = useRef(null);
  const completed = useRef(null);
  const approval = useRef(onApproved);
  approval.current = onApproved;

  useEffect(() => {
    let cancelled = false;
    pointApi.terminales().then(data => {
      if (cancelled) return;
      setEnabled(data.enabled); setTerminales(data.terminales);
      const available = data.terminales.filter(t => t.disponible);
      if (data.terminales.length === 1 && available.length === 1) setTerminalId(available[0].id);
    }).catch(e => { if (!cancelled) { setEnabled(e.response?.data?.enabled === true); setError(e.response?.data?.message || e.message); } });
    return () => { cancelled = true; };
  }, [cobro?.activo]);

  const accept = useCallback((data, notifyCompletion = true) => {
    setCobro(data);
    if (data && !data.activo) request.current = null;
    if (data?.terminalId) setTerminalId(data.terminalId);
    if (data) setMedioPago(data.medioPago || 'all');
    if (notifyCompletion && data && !data.activo &&
        ['aprobado', 'cancelado', 'rechazado', 'expirado'].includes(data.estado) && completed.current !== data.id) {
      completed.current = data.id;
      setResultado(data);
    }
    if (data?.aprobado && notified.current !== data.id) {
      notified.current = data.id; approval.current?.(data.boletaId);
    }
  }, []);

  useEffect(() => {
    const current = ++generation.current;
    setCobro(null); setResultado(null); request.current = null; setError('');
    if (!boletaId) { setRecovering(false); return; }
    setRecovering(true);
    pointApi.porBoleta(boletaId).then(data => {
      if (generation.current === current) accept(data, false);
    }).catch(e => {
      if (generation.current === current) setError(e.response?.data?.message || e.message);
    }).finally(() => { if (generation.current === current) setRecovering(false); });
    return () => { generation.current = current + 1; };
  }, [boletaId, accept]);

  useEffect(() => {
    if (!cobro?.activo || !boletaId) return;
    const current = generation.current;
    let cancelled = false;
    let querying = false;
    const poll = async () => {
      if (querying) return;
      querying = true;
      try {
        const data = await pointApi.consultar(cobro.id);
        if (!cancelled && generation.current === current) { accept(data); setError(''); }
      } catch (e) {
        if (!cancelled && generation.current === current) setError(e.response?.data?.message || e.message);
      } finally { querying = false; }
    };
    const timer = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [cobro?.id, cobro?.activo, boletaId, accept]);

  const crear = async options => {
    if (submitting.current || recovering) return;
    submitting.current = true; setLoading(true); setError('');
    const current = generation.current;
    if (!request.current) request.current = { terminalId, medioPago, ...options, idempotencia: crypto.randomUUID() };
    try {
      const data = await pointApi.crear(boletaId, request.current);
      if (generation.current === current) {
        accept(data); if (!data.activo) request.current = null;
      }
    } catch (e) {
      if (generation.current === current) {
        setError(e.response?.data?.message || e.message);
        // Recover the reserved attempt even when the HTTP creation response was lost.
        try {
          const recovered = await pointApi.porBoleta(boletaId);
          if (generation.current === current) accept(recovered);
        } catch (_) { /* keep retry key */ }
        if (e.response?.status >= 400 && e.response?.status < 500) request.current = null;
      }
    } finally { submitting.current = false; setLoading(false); }
  };

  const cancelar = async () => {
    if (!cobro?.activo || submitting.current) return;
    submitting.current = true; setLoading(true); setError('');
    const current = generation.current;
    try {
      const data = await pointApi.cancelar(cobro.id);
      if (generation.current === current) accept(data);
    } catch (e) { if (generation.current === current) setError(e.response?.data?.message || e.message); }
    finally { submitting.current = false; setLoading(false); }
  };

  const seleccionarTerminal = id => { if (!cobro?.activo && !submitting.current) { request.current = null; setTerminalId(id); } };
  const seleccionarMedio = medio => { if (!cobro?.activo && !submitting.current) { request.current = null; setMedioPago(medio); } };
  const limpiar = () => {
    generation.current += 1;
    request.current = null;
    setCobro(null); setResultado(null); setError(''); setRecovering(false);
    setMedioPago('all');
    const available = terminales.filter(t => t.disponible);
    setTerminalId(terminales.length === 1 && available.length === 1 ? available[0].id : '');
  };
  return { enabled, terminales, terminalId, seleccionarTerminal, medioPago, seleccionarMedio, cobro, resultado, limpiar,
    loading, recovering, error, crear, cancelar };
}
